"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const IconButton_1 = require("../IconButton");
const Target_1 = require("../Target");
const NumberButton = ({ active = false, onPageChange, page, disabled = false }) => {
    var _a, _b;
    const theme = (0, foundations_1.useTheme)();
    const handleClick = react_1.default.useCallback(() => {
        onPageChange(page);
    }, [onPageChange, page]);
    // mimicked transparent icon button styles
    const buttonStyles = Object.assign({ 
        // layout
        display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "iconButtonBorderRadiusSquare", 
        // appearance
        color: !disabled
            ? ((_b = (_a = theme.colors) === null || _a === void 0 ? void 0 : _a.iconButtonColor) !== null && _b !== void 0 ? _b : "iconButtonColorPrimary")
            : "disabled01", borderWidth: 1, borderStyle: "solid", borderColor: "transparent", transition: "colorMedium", px: 4, background: active ? "iconButtonBackgroundColor" : "transparent" }, (active
        ? {}
        : {
            "&:hover": {
                background: !disabled ? "iconButtonBackgroundColor" : undefined,
                borderColor: "transparent"
            },
            "&:active": {
                background: !disabled ? "iconButtonBackgroundColor" : undefined,
                borderColor: "transparent"
            },
            "&:focus-visible": {
                borderColor: "iconButtonFocusBorderColor"
            },
            "&:disabled": {
                background: "buttonBackgroundColorDisabled"
            }
        }));
    return (react_1.default.createElement(Target_1.Target, { "aria-selected": active, "aria-label": `Go to page ${page}`, role: "tab", disabled: disabled, sx: buttonStyles, onClick: handleClick }, page));
};
const getNextPrevButtonDisplay = (hidden) => hidden ? "none" : "flex";
const NumberedPagination = ({ continuous, count, handleClickNext, handleClickPrev, handleKeyUp, handlePageChange, hasNext, hasPrev, hideNextPrevButtonsArray, page, siblingCount = 1, disabled }) => {
    // https://github.com/mui/material-ui/blob/master/packages/mui-material/src/usePagination/usePagination.js
    const getPageNumbers = () => {
        const boundaryCount = 1; // the number of always visible pages at the beginning and end
        const range = (start, end) => {
            const length = end - start + 1;
            return Array.from({ length }, (_, i) => start + i);
        };
        const startPages = range(1, Math.min(boundaryCount, count));
        const endPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count);
        const siblingsStart = Math.max(Math.min(
        // Natural start
        page - siblingCount, 
        // Lower boundary when page is high
        count - boundaryCount - siblingCount * 2 - 1), 
        // Greater than startPages
        boundaryCount + 2);
        const siblingsEnd = Math.min(Math.max(
        // Natural end
        page + siblingCount, 
        // Upper boundary when page is low
        boundaryCount + siblingCount * 2 + 2), 
        // Less than endPages
        count - boundaryCount - 1);
        const pages = [
            ...startPages,
            // Start ellipsis
            ...(siblingsStart > boundaryCount + 2
                ? ["..."]
                : boundaryCount + 1 < count - boundaryCount
                    ? [boundaryCount + 1]
                    : []),
            // Sibling pages
            ...Array.from({ length: siblingsEnd - siblingsStart + 1 }, (_, i) => siblingsStart + i),
            // End ellipsis
            ...(siblingsEnd < count - boundaryCount - 1
                ? ["..."]
                : count - boundaryCount > boundaryCount
                    ? [count - boundaryCount]
                    : []),
            ...endPages
        ];
        return pages;
    };
    const pageNumbers = getPageNumbers();
    return (react_1.default.createElement(foundations_1.Box, { sx: {
            display: "flex",
            alignItems: "center",
            gap: 4
        } },
        react_1.default.createElement(foundations_1.Box, { onKeyUp: handleKeyUp, role: "tablist", sx: {
                display: "flex",
                flex: 1,
                gap: 2,
                justifyContent: "center",
                listStyle: "none",
                minHeight: 10
            } },
            react_1.default.createElement(IconButton_1.IconButton, { icon: "caret-left", shape: "square", variant: "transparent", "aria-label": "Go to previous page", onClick: handleClickPrev, role: "tab", tabIndex: hasPrev ? 0 : -1, disabled: disabled || (!hasPrev && !continuous), sx: {
                    display: [
                        getNextPrevButtonDisplay(hideNextPrevButtonsArray[0]),
                        getNextPrevButtonDisplay(hideNextPrevButtonsArray[1]),
                        getNextPrevButtonDisplay(hideNextPrevButtonsArray[2])
                    ],
                    flexGrow: 0,
                    gap: 2,
                    background: "transparent"
                } }),
            pageNumbers.map((pageNumber, index) => {
                if (pageNumber === "...") {
                    return (react_1.default.createElement(foundations_1.Box, { key: `ellipsis-${index}`, sx: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 4,
                            whiteSpace: "nowrap",
                            color: !disabled ? "text01" : "disabled01"
                        } }, "..."));
                }
                return (react_1.default.createElement(NumberButton, { key: `page-${pageNumber}`, active: page === pageNumber, page: pageNumber, onPageChange: handlePageChange, disabled: disabled }));
            }),
            react_1.default.createElement(IconButton_1.IconButton, { icon: "caret-right", shape: "square", variant: "transparent", "aria-label": "Go to next page", onClick: handleClickNext, role: "tab", tabIndex: hasNext ? 0 : -1, disabled: disabled || (!hasNext && !continuous), sx: {
                    display: [
                        getNextPrevButtonDisplay(hideNextPrevButtonsArray[0]),
                        getNextPrevButtonDisplay(hideNextPrevButtonsArray[1]),
                        getNextPrevButtonDisplay(hideNextPrevButtonsArray[2])
                    ],
                    flexGrow: 0,
                    gap: 2
                } }))));
};
exports.default = NumberedPagination;
//# sourceMappingURL=NumberedPagination.js.map